import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import classNames from "classnames";

import bankInfoStyles from "./WaffyBankInfoModal.module.scss";
import formStyles from "./MBTPaymentModal.module.scss";
import PrimaryButton from "../../../components/common/PrimaryButton";
import ImageUpload from "../../../components/forms/imageUpload";
import { IContractDetailsResponseData } from "../../../setup/api/services/contract/models";
import { PartyRow } from "../create/partyTable";
import { submitBankTransfer } from "./submitBankTransfer";

const WAFFY_IBAN = "SA1880000609608018999883";

interface Props {
  open: boolean;
  onClose: () => void;
  customerAmount?: number;
  contract?: IContractDetailsResponseData;
  customerRow?: PartyRow;
  beneficiaryId?: string;
  beneficiaryName?: string;
}

const MBTPaymentModal = ({
  open,
  onClose,
  customerAmount,
  contract,
  customerRow,
  beneficiaryId,
  beneficiaryName,
}: Props) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState(-1);
  const [images, setImages] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);

  const bankData = [
    {
      title: t("lbl_add_bank_account_written_bank_name"),
      value: t("lbl_development_bank"),
    },
    {
      title: t("lbl_add_bank_account_user_name"),
      value: t("lbl_waffy_company"),
    },
    {
      title: t("IBAN"),
      value: "SA1880000609608018999883",
    },
    {
      title: t("lbl_amount_to_be_transferred"),
      value: customerAmount !== undefined ? String(customerAmount) : "-",
      noCopy: true,
    },
  ];

  const copyText = useCallback(async (value: string, index: number) => {
    try {
      await navigator?.clipboard?.writeText(value);
      setSelected(index);
      setTimeout(() => setSelected(-1), 2000);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const tempImages = [...images];
    const allowedFormats = [
      "image/x-png",
      "image/jpeg",
      "image/jpg",
      "image/webp",
      "image/png",
      "application/pdf",
    ];
    const totalFiles = Math.min(
      3 - tempImages.length,
      e?.target?.files?.length || 0
    );
    for (let i = 0; i < totalFiles; i++) {
      const file = e?.target?.files?.[i];
      if (file && allowedFormats.includes(file.type)) {
        tempImages.push(file);
      }
    }
    setImages(tempImages);
  };

  const handleDeleteImage = (index: number) => {
    const tempImages = [...images];
    tempImages.splice(index, 1);
    setImages(tempImages);
  };

  const handleClose = () => {
    setStep(1);
    setImages([]);
    onClose();
  };

  const handleSubmit = async () => {
    if (!contract || !customerRow) return;
    try {
      setLoading(true);
      await submitBankTransfer(
        images,
        contract,
        customerRow,
        beneficiaryId ?? "-1",
        beneficiaryName ?? "WAFFY_SYSTEM",
        WAFFY_IBAN
      );
      handleClose();
      window.location.reload();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const primaryButtonStyle = {
    height: "44px",
    borderRadius: "8px",
    textAlign: "center" as const,
    whiteSpace: "nowrap" as const,
    fontSize: "14px",
    lineHeight: "100%",
    fontWeight: 500,
    backgroundColor: "#0d44ad",
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className={bankInfoStyles.container_wrapper}>
        <IconButton aria-label="close" onClick={handleClose}>
          <CloseIcon />
        </IconButton>

        {step === 1 && (
          <>
            <p className={bankInfoStyles.title}>{t("lbl_waffy_bank_info")}</p>
            <div className={bankInfoStyles.content}>
              {bankData.map((item, index) => (
                <div
                  key={index}
                  onClick={() => !item.noCopy && copyText(item.value, index)}
                  className={bankInfoStyles.section}
                  style={{ cursor: item.noCopy ? "default" : "pointer" }}>
                  <div>
                    {!item.noCopy && (
                      <img
                        alt="copy"
                        src={
                          selected === index
                            ? "/assets/images/svg/tick.svg"
                            : "/assets/images/svg/copy-icon.svg"
                        }
                      />
                    )}
                    <p className={classNames("ml2", "p2")}>{item.value}</p>
                  </div>
                  <p className={classNames("g600", "p2")}>{item.title}</p>
                </div>
              ))}
            </div>
            <div className={formStyles.bottom_section}>
              <PrimaryButton
                onClick={() => setStep(2)}
                style={{ ...primaryButtonStyle, flex: 2 }}>
                {t("lbl_continue")}
              </PrimaryButton>
              <PrimaryButton
                onClick={handleClose}
                style={{
                  flex: 1,
                  background: "#F3F6FC",
                  color: "#0D44AD",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: 500,
                  height: "44px",
                }}>
                {t("lbl_close")}
              </PrimaryButton>
            </div>
          </>
        )}

        {step === 2 && (
          <div className={formStyles.form}>
            <div className={formStyles.info_card}>
              <div className={formStyles.image_container}>
                <img
                  src="/assets/images/svg/info-outlined.svg"
                  alt="info_bank"
                />
              </div>
              <div className={formStyles.text_wrapper}>
                <p className={classNames("p3", "g900")}>
                  {t("lbl_bank_transfer_warning")}
                </p>
              </div>
            </div>
            <div className={formStyles.top_section}>
              <div className={formStyles.image_text}>
                <h3>{t("lbl_transfer_receipt")}</h3>
                <p className="hint">{t("lbl_attach_upto_3_files")}</p>
              </div>
              <div className={formStyles.form_scroll}>
                {images.length < 3 && (
                  <ImageUpload
                    withPDF
                    multiple
                    handleChange={handleImageUpload}
                  />
                )}
                {images.map((file, index) => (
                  <div
                    key={index}
                    className={formStyles.uploaded_image_container}>
                    {file.type === "application/pdf" ? (
                      <embed
                        src={URL.createObjectURL(file)}
                        type="application/pdf"
                        className={formStyles.uploaded_image}
                      />
                    ) : (
                      <img
                        className={formStyles.uploaded_image}
                        src={URL.createObjectURL(file)}
                        alt="upload"
                      />
                    )}
                    <IconButton
                      onClick={() => handleDeleteImage(index)}
                      className={formStyles.delete_icon}
                      aria-label="delete picture">
                      <img src="/assets/images/svg/bin.svg" alt="bin" />
                    </IconButton>
                  </div>
                ))}
              </div>
            </div>
            <div className={formStyles.bottom_section}>
              <PrimaryButton
                onClick={handleSubmit}
                style={{
                  ...primaryButtonStyle,
                  flex: 2,
                  backgroundColor:
                    images.length === 0 || loading ? "#e7e9ea" : "#0d44ad",
                  color: images.length === 0 || loading ? "#707a82" : undefined,
                }}
                disabled={images.length === 0 || loading}>
                {t("lbl_send_details")}
              </PrimaryButton>
              <PrimaryButton
                style={{
                  flex: 1,
                  background: "#F3F6FC",
                  color: "#0D44AD",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: 500,
                  height: "44px",
                }}
                onClick={() => setStep(1)}>
                {t("lbl_delete_transaction_back_btn")}
              </PrimaryButton>
            </div>
          </div>
        )}
      </Box>
    </Modal>
  );
};

export default MBTPaymentModal;
